import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import isMobile from 'ismobilejs';

import MessengerButton from './MessengerButton';
import Header from './Header';
import Conversation from './ConversationContainer';
import Settings from './Settings';
import Channel from './channels/Channel';
import ErrorNotification from './ErrorNotification';
import ChatInput from './ChatInput';
import MessageIndicator from './MessageIndicator';
import Webview from './Webview';
import UploadMenu from './UploadMenu';
import Introduction from './Introduction';

import { resetUnreadCount } from '../actions/conversation';
import { hasChannels } from '../utils/app';
import { DISPLAY_STYLE, ANIMATION_TIMINGS } from '../constants/styles';
import { WIDGET_STATE } from '../constants/app';
import { disableAnimation } from '../actions/app-state';
import ConnectionErrorBanner from './ConnectionErrorBanner';

/* Bookboost Begin */
import ProactiveMessagesContainer from './bookboost/proactive-messages-container';
/* Bookboost End */

export class WidgetComponent extends Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        config: PropTypes.object.isRequired,
        widgetSize: PropTypes.string.isRequired,
        appState: PropTypes.object.isRequired,
        isWebviewShown: PropTypes.bool.isRequired,
    };

    _inputRef = React.createRef();

    onTouchStart = (e) => {
        this.props.dispatch(resetUnreadCount());
        // the behavior is problematic only on iOS devices
        if (this._inputRef.current && isMobile().apple.device) {
            const inputNode = this._inputRef.current;

            // only blur if touching outside of the footer
            if (!inputNode.contains(e.target)) {
                inputNode.blur();
            }
        }
    };

    onClick = () => {
        this.props.dispatch(resetUnreadCount());
    };

    handleResize = () => {
        this.props.dispatch(disableAnimation());
    };

    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    render() {
        const { appState, config, widgetSize, isWebviewShown } = this.props;
        const { isBrandColorDark, isAccentColorDark, isLinkColorDark } = config.colors;
        const displayStyle = config.displayStyle || 'button';

        const settingsComponent = appState.settingsVisible ? (
            <CSSTransition classNames="settings" timeout={250} key="settings">
                <Settings />
            </CSSTransition>
        ) : null;

        // if no user set in store or the app has no channels,
        // no need to render the channel page manager
        const channelsComponent = hasChannels(config) ? <Channel /> : null;

        const footer = appState.settingsVisible ? null : <ChatInput footerRef={this._inputRef} />;

        const classNames = [`${displayStyle}-display`];

        if (appState.widgetState === WIDGET_STATE.OPENED) {
            classNames.push('appear');
        } else if (appState.widgetState === WIDGET_STATE.CLOSED) {
            classNames.push('close');
        } else if (appState.widgetState === WIDGET_STATE.EMBEDDED) {
            classNames.push('embedded');
        } else {
            // state is WIDGET_STATE.INIT
            classNames.push('init');
        }

        if (isMobile().apple.device) {
            classNames.push('ios-device');
        }

        if (appState.showAnimation) {
            classNames.push('animation');
        }

        const notification = appState.errorNotificationMessage ? (
            <CSSTransition classNames="notification" timeout={500}>
                <ErrorNotification message={appState.errorNotificationMessage} />
            </CSSTransition>
        ) : null;

        const wrapperClassNames = [
            `branding-color-${isBrandColorDark ? 'dark' : 'light'}`,
            `accent-color-${isAccentColorDark ? 'dark' : 'light'}`,
            `link-color-${isLinkColorDark ? 'dark' : 'light'}`,
        ];

        let messengerButton;

        if (displayStyle === DISPLAY_STYLE.BUTTON && appState.widgetState !== WIDGET_STATE.EMBEDDED) {
            messengerButton = <MessengerButton shown={appState.widgetState !== WIDGET_STATE.OPENED} />;
        }

        const webview = isWebviewShown ? (
            <CSSTransition classNames="webview" timeout={ANIMATION_TIMINGS.WEBVIEW_TRANSITION}>
                <Webview />
            </CSSTransition>
        ) : null;

        const uploadAttachment = appState.uploadVisible ? (
            <CSSTransition classNames="upload" timeout={0}>
                <UploadMenu />
            </CSSTransition>
        ) : null;

        // show the introduction pane outside the conversation component if it is fixed
        const introduction = config.fixedIntroPane ? <Introduction /> : null;

        /* Bookboost Begin */
        const isIOS15 = navigator.userAgent.indexOf("iPhone OS 15") !== -1;
        const wrapperStyle = isIOS15 ? {paddingBottom: '53px'} : {};
        /* Bookboost End */

        return (
            <div className={`widget-${widgetSize}`}>
            	{/* Bookboost Begin */}
                <ProactiveMessagesContainer />
                {/* Bookboost End */}
                <div
                    id="container"
                    className={classNames.join(' ')}
                    onTouchStart={this.onTouchStart}
                    onClick={this.onClick}
                >
                    <MessageIndicator />
                    {/* Bookboost: Added style: Padding on bottom if device is iOS 15 */}
                    <div id="wrapper" className={wrapperClassNames.join(' ')} style={wrapperStyle}>
                        <Header />
                        <TransitionGroup className="notification-container">{notification}</TransitionGroup>
                        <TransitionGroup>{settingsComponent}</TransitionGroup>
                        <TransitionGroup>{uploadAttachment}</TransitionGroup>
                        {channelsComponent}
                        <ConnectionErrorBanner />
                        {introduction}
                        <Conversation />
                        {footer}
                        <TransitionGroup>{webview}</TransitionGroup>
                    </div>
                </div>
                {messengerButton}
            </div>
        );
    }
}

export default connect(
    ({
        appState: { uploadVisible, settingsVisible, widgetState, errorNotificationMessage, showAnimation, widgetSize },
        config,
        webview,
    }) => {
        // only extract what is needed from appState as this is something that might
        // mutate a lot
        return {
            appState: {
                uploadVisible,
                settingsVisible,
                widgetState,
                errorNotificationMessage,
                showAnimation,
            },
            config,
            widgetSize,
            isWebviewShown: webview.isShown,
        };
    },
    null,
    null,
    { forwardRef: true },
)(WidgetComponent);
